/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import UserTable from "./components/UserTable";
import { Auth } from "aws-amplify";
import axios from "axios";
import { Box } from "@chakra-ui/react";

interface Filters {
  [key: string]: any;
}

const initialFilters: Filters = {
  email: "",
  username: "",
  status: "",
  createdDateRange: [null, null],
  merchant_id: "",
  subaccount_id: "",
  agent_id: "",
};

const UserManagement = () => {
  const role = localStorage.getItem("userRole");
  const [currentUsername, setCurrentUsername] = useState("");

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const init = async () => {
      const userInfo = await Auth.currentAuthenticatedUser();
      setCurrentUsername(userInfo.username);
    };
    init();
  }, []);

  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(10);

  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    if (currentUsername) {
      const signal = getAbortSignal();
      fetchUsers(false, signal);
    }
  }, [page, pageSize, currentUsername]);

  const fetchUsers = async (clear = false, signal?: AbortSignal) => {
    try {
      setIsLoading(true);
      const { email, username, status, createdDateRange, merchant_id, subaccount_id, agent_id } = !clear
        ? filters
        : initialFilters;

      const startDate = createdDateRange[0]?.toISOString() || "";
      const endDate = createdDateRange[1]?.toISOString() || "";

      const params = {
        email,
        username,
        status,
        startDate,
        endDate,
        merchant_id,
        subaccount_id,
        agent_id,
        page: ((!clear ? page : 0) + 1).toString(),
        pageSize: pageSize.toString(),
        currentUsername,
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/get-all-users`,
        { params, signal }
      );
      const { data, totalCount } = response.data;

      setTableData(data ?? []);
      setRowCount(totalCount ?? 0);
      setIsLoading(false);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error("Error fetching users:", error);
      setTableData([]);
      setIsLoading(false);
      setRowCount(0);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchUsers();
  };

  const handleClear = () => {
    setFilters(initialFilters);
    setTableData([]);
    setPage(0);
    fetchUsers(true);
  };

  const setPaginationModel = (newValue: any) => {
    setPage(newValue.page);
    setPageSize(newValue.pageSize);
  };

  const triggerReloadUserList = () => {
    fetchUsers();
  };

  return (
    <Box sx={{ position: "relative" }}>
      <UserTable
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        tableData={tableData}
        setTableData={setTableData}
        setRowCount={setRowCount}
        setPaginationModel={setPaginationModel}
        currentUsername={currentUsername}
        currentUserRole={role}
        handleSearch={handleSearch}
        handleClear={handleClear}
        initialFilters={initialFilters}
        filters={filters}
        setFilters={setFilters}
        setIsLoading={setIsLoading}
        fetchUsers={fetchUsers}
        triggerReloadUserList={triggerReloadUserList}
      />
    </Box>
  );
};

export default UserManagement;
